import classNames from 'classnames';
import CustomIcon from '@/icons/custom.svg?react';
import { ChangeEvent } from 'react';
import Tooltip from './Tooltip/Tooltip';
import Input from './Input/Input';
import { useTranslation } from 'react-i18next';

interface Props {
  isEditing: boolean;
  value: string;
  onChange: React.ChangeEventHandler<HTMLInputElement>;
  onBlur?: React.ChangeEventHandler<HTMLInputElement>;
  error: string;
  touched: boolean;
  testId: string;
  name: string;
  isCustom: boolean;
  label?: string;
  inputWrapperClassName?: string;
  labelClassName?: string;
  notAvailable?: boolean;
  iconRight?: boolean;
}

export const COACell = ({
  isEditing,
  value,
  onChange,
  onBlur,
  error,
  touched,
  testId,
  name,
  isCustom,
  label,
  inputWrapperClassName = '',
  labelClassName = '',
  notAvailable,
  iconRight,
}: Props) => {
  const { t } = useTranslation();
  const customValueIndicator = isCustom ? (
    <Tooltip
      content={
        <div className='whitespace-pre-line text-center'>
          {t('Custom value has been applied')}
        </div>
      }
      wrapperClassName='flex items-center justify-center whitespace-pre-line'
    >
      <CustomIcon data-testid={`${name}-custom-icon`} />
    </Tooltip>
  ) : null;

  return (
    <div className={`max-w-[172px] ${inputWrapperClassName}`}>
      {label && (
        <div className={`mb-2 text-gray-400 ${labelClassName}`}>{label}</div>
      )}
      {isEditing ? (
        <div className='relative'>
          <Input
            name={name}
            className={classNames(
              'max-w-[136px] !border-gray-500 !p-3 !text-base',
              {
                '!pr-8': isCustom,
              }
            )}
            onChange={(e) => {
              if (isNaN(Number((e.target as HTMLInputElement).value))) {
                return;
              }
              (e.target as HTMLInputElement).value = (
                e.target as HTMLInputElement
              ).value.replace(/-|\+/g, '');
              onChange(e as ChangeEvent<HTMLInputElement>);
            }}
            onBlur={onBlur}
            value={value}
            error={error}
            touched={touched}
            data-testid={testId}
            labelClassName='text-gray-400'
            disabled={notAvailable}
          />
          {!notAvailable && (
            <div className='absolute right-2 top-[calc(calc(100%-16px)/2)] translate-y-[-50%]'>
              {customValueIndicator}
            </div>
          )}
        </div>
      ) : (
        <div
          data-testid={name + '_value'}
          className={classNames('flex items-center gap-x-2 pb-4', {
            'text-gray-700': notAvailable,
          })}
        >
          {notAvailable ? (
            t('Not available')
          ) : (
            <>
              {!iconRight && customValueIndicator} {value || '-'}{' '}
              {iconRight && customValueIndicator}
            </>
          )}
        </div>
      )}
    </div>
  );
};
